/**
 * Pictures, official channels and videos for the leagues, keyed by league id
 * (see LEAGUES in records.ts).
 *
 * Images are copies of freely licensed Wikimedia Commons files, resized to
 * 1280 px and kept in static/images/leagues/, each with a 640 px copy for
 * screens that do not need the full size. Each carries the credit its
 * licence asks for; content/pages/image-credits.md lists them all, and
 * research/image-credits.json keeps what the Commons API reported. A free
 * licence does not cover the logos in a photo, so none is used as a logo.
 *
 * Channels are the accounts each league links from its own website. Videos
 * come from each official channel's YouTube feed, checked 2026-10-15. They
 * play in the page (see playInPage in watch.ts).
 */

export interface Credit {
  author: string;
  licence: string;
  licenceUrl?: string;
  /** The file's page on Commons. */
  page: string;
}

export interface Picture {
  /** The 1280 px file. A 640 px copy sits beside it as <name>-640.jpg (see pictureSources in watch.ts). */
  src: string;
  width: number;
  height: number;
  alt: string;
  caption: string;
  credit: Credit;
}

export type Channel = "youtube" | "x" | "instagram" | "tiktok" | "twitch" | "facebook" | "linkedin";

export interface Video {
  id: string;
  title: string;
  date: string;
}

export interface LeagueMedia {
  picture?: Picture;
  channels?: Partial<Record<Channel, string>>;
  videos?: Video[];
}

export const CHANNEL_LABELS: Record<Channel, string> = {
  youtube: "YouTube",
  x: "X",
  instagram: "Instagram",
  tiktok: "TikTok",
  twitch: "Twitch",
  facebook: "Facebook",
  linkedin: "LinkedIn",
};

const CC_BY_SA_4 = { licence: "CC BY-SA 4.0", licenceUrl: "https://creativecommons.org/licenses/by-sa/4.0/" };
const commons = (file: string) => `https://commons.wikimedia.org/wiki/File:${encodeURIComponent(file.replace(/ /g, "_"))}`;

export const MEDIA: Record<string, LeagueMedia> = {
  battlebots: {
    picture: {
      src: "/images/leagues/battlebots.jpg",
      width: 1280,
      height: 815,
      alt: "The BattleBots Arena building in Las Vegas, with the BattleBots name across its front",
      caption: "The BattleBots Arena, Las Vegas",
      credit: { author: "TaurusEmerald", ...CC_BY_SA_4, page: commons("BattleBots Arena Las Vegas, Nevada.jpg") },
    },
    channels: {
      youtube: "https://www.youtube.com/@BattleBots",
      x: "https://x.com/BattleBots",
      instagram: "https://www.instagram.com/battlebots/",
      tiktok: "https://www.tiktok.com/@battlebots",
      twitch: "https://www.twitch.tv/battlebots",
      facebook: "https://www.facebook.com/battlebots",
    },
    videos: [
      { id: "81aUmLEcCV0", title: "All the fights from the Pro League quarter-finals", date: "2026-10-12" },
      { id: "8XL_fE84pB4", title: "Quarter-final chaos: Pro League episode 15", date: "2026-10-08" },
      { id: "r0UMxKeTAOI", title: "All the fights from the Pro League group stage", date: "2026-10-05" },
    ],
  },
  nhrl: {
    channels: { youtube: "https://www.youtube.com/@NHRL" },
    videos: [
      { id: "KmhKE_jfEBM", title: "Xenomorph tore this robot wide open", date: "2026-10-08" },
      { id: "AJM9Kdbzmn4", title: "NHRL Prime Time, round 6", date: "2026-10-04" },
      { id: "4UAyypqpgIA", title: "Goose takes down the number one ranked bot", date: "2026-09-29" },
    ],
  },
  robocup: {
    picture: {
      src: "/images/leagues/robocup-msl.jpg",
      width: 1280,
      height: 852,
      alt: "Wheeled soccer robots contest the ball in the Middle Size League final, RoboCup 2013 in Eindhoven",
      caption: "Middle Size League final, RoboCup 2013, Eindhoven",
      credit: { author: "Bart van Overbeeke Fotografie", licence: "CC BY-SA 2.0", licenceUrl: "https://creativecommons.org/licenses/by-sa/2.0/", page: commons("Final Match MSL Robot Soccer (RoboCup 2013 in Eindhoven).jpg") },
    },
    channels: {
      youtube: "https://www.youtube.com/channel/UCu0u7DbHJlvATLHjoANa3wQ",
      x: "https://x.com/robocup_org",
      facebook: "https://www.facebook.com/robocup.org",
      linkedin: "https://www.linkedin.com/company/robocup-org",
    },
    videos: [
      { id: "XuEdf0fMAME", title: "While the world watched the FIFA World Cup, robots made history at RoboCup 2026", date: "2026-07-05" },
      { id: "bbWUC995Iho", title: "RoboCup 2026 finals, live stream", date: "2026-07-05" },
      { id: "4ww0UqLBaLE", title: "RoboCup 2027 Nuremberg trailer", date: "2026-07-06" },
      { id: "4QtBSDSC2pk", title: "RoboCup 1997 to 2011", date: "2018-09-19" },
    ],
  },
  "robocup-humanoid-league": {
    picture: {
      src: "/images/leagues/robocup.jpg",
      width: 1280,
      height: 1738,
      alt: "The NimbRo-OP2X humanoid robot playing a Tsinghua robot on the pitch at RoboCup 2018",
      caption: "NimbRo-OP2X against Tsinghua, RoboCup 2018, Montreal",
      credit: { author: "Sven Behnke", ...CC_BY_SA_4, page: commons("NimbRo-OP2X Humanoid Soccer Robot at RoboCup 2018 in Montreal.jpg") },
    },
  },
  first: {
    picture: {
      src: "/images/leagues/first.jpg",
      width: 1280,
      height: 960,
      alt: "The field for the 2025 FIRST Robotics Competition game Reefscape, set up in a gym",
      caption: "A Reefscape field, FIRST Robotics Competition 2025",
      credit: { author: "Ismart733", licence: "CC0", licenceUrl: "https://creativecommons.org/publicdomain/zero/1.0/", page: commons("FRC field 2025MITVC.jpg") },
    },
    channels: {
      youtube: "https://www.youtube.com/user/FIRSTWorldTube",
      instagram: "https://www.instagram.com/first_official_/",
      tiktok: "https://www.tiktok.com/@first_inspires",
      facebook: "https://www.facebook.com/FIRSTOfficial",
    },
    videos: [
      { id: "1TqkxAoQQjg", title: "2026 FIRST Championship recap", date: "2026-05-27" },
      { id: "6WbqxaOQFDo", title: "FIRST Robotics Competition in two minutes", date: "2026-05-27" },
      { id: "boJWcbOxato", title: "2026-2027 FIRST season reveal", date: "2026-05-02" },
    ],
  },
  vex: {
    picture: {
      src: "/images/leagues/vex.jpg",
      width: 1280,
      height: 853,
      alt: "A student-built VEX competition robot on its field",
      caption: "A VEX Robotics Competition robot, 2017-18 season",
      credit: { author: "Noah Wulf", ...CC_BY_SA_4, page: commons("VEX Robotics Competition Robot.jpg") },
    },
  },
  "indy-autonomous-challenge": {
    picture: {
      src: "/images/leagues/indy-autonomous-challenge.jpg",
      width: 1280,
      height: 976,
      alt: "A Dallara IAC AV-24 autonomous race car in the team garage, with engineers at laptops behind it",
      caption: "A Dallara IAC AV-24 in the garage",
      credit: { author: "Calreyn88", ...CC_BY_SA_4, page: commons("Dallara IAC AV-24.jpg") },
    },
    channels: {
      youtube: "https://www.youtube.com/channel/UCunVdBI6YC7TMICjdaaxAjA",
      x: "https://x.com/indyAchallenge",
      instagram: "https://www.instagram.com/IndyAChallenge/",
      facebook: "https://www.facebook.com/indyAchallenge",
    },
    videos: [
      { id: "d1fkCSmIgtw", title: "Race highlights, Laguna Seca 2026", date: "2026-09-16" },
      { id: "in3jJiLSWvw", title: "Modena track record: the AV-24 in 58.3 seconds", date: "2025-06-23" },
      { id: "NOTF7Pi-SxI", title: "Autonomous land speed record in a Maserati MC20", date: "2025-03-03" },
    ],
  },
  a2rl: {
    picture: {
      src: "/images/leagues/a2rl.jpg",
      width: 1280,
      height: 853,
      alt: "A2RL's driverless Dallara EAV24 race car on track at Suzuka",
      caption: "A2RL's Dallara EAV24 at Suzuka, 2024",
      credit: { author: "Morio", ...CC_BY_SA_4, page: commons("EAV24 Yalla 2024 Suzuka front-right.jpg") },
    },
    channels: {
      youtube: "https://www.youtube.com/channel/UCx07pX3iq_-cCk71u1Vtx2Q",
      x: "https://x.com/A2RLeague",
      instagram: "https://www.instagram.com/autonomousracingleague/",
      tiktok: "https://www.tiktok.com/@autonomousracingleague",
      twitch: "https://www.twitch.tv/autonomousracingleague",
    },
    videos: [
      { id: "VSdCF_yR3L4", title: "The A2RL cars at Imola, 2026", date: "2026-09-07" },
      { id: "uaKp90HjVbw", title: "How the season unfolded", date: "2026-04-24" },
      { id: "qtRUoYxZnjE", title: "The fastest multi-drone race yet: A2RL Drone Championship 2026", date: "2026-01-26" },
    ],
  },
  "drone-champions-league": {
    channels: {
      youtube: "https://www.youtube.com/dronechampionsleague",
      x: "https://x.com/DroneChampionsL",
      instagram: "https://www.instagram.com/dronechampionsleague/",
      tiktok: "https://www.tiktok.com/@dronechampionsleague",
      facebook: "https://www.facebook.com/dronechampionsleague",
    },
    videos: [
      { id: "fyQn7JyT374", title: "Eagle Cup Finals 2026: four teams, one champion", date: "2026-09-20" },
      { id: "A1SkXdNJWG4", title: "Saudi Drone Champions League 2026 final", date: "2026-09-05" },
      { id: "5PwfJW8iBC4", title: "Hawk Cup qualifying: Cyclone Racing against Squadron78", date: "2026-10-14" },
    ],
  },
  "university-rover-challenge": {
    picture: {
      src: "/images/leagues/university-rover-challenge.jpg",
      width: 1280,
      height: 720,
      alt: "Teams and their rovers in a group photo at the Mars Desert Research Station, Utah",
      caption: "University Rover Challenge 2017, Mars Desert Research Station",
      credit: { author: "The Mars Society", ...CC_BY_SA_4, page: commons("Mars Society University Rover Challenge 2017.jpg") },
    },
  },
};

export const youtubeUrl = (id: string) => `https://www.youtube.com/watch?v=${id}`;
export const youtubeThumb = (id: string) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
/**
 * The privacy-enhanced player, admitted by security.csp.frame_src in site.yaml.
 * `next` queues more videos with YouTube's playlist parameter, which plays
 * them in order after the first.
 */
export const youtubeEmbed = (id: string, next: string[] = []) =>
  `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0${next.length ? `&playlist=${next.join(",")}` : ""}`;
